import {Avatar, DatePicker, Input, Tag} from 'antd'
import EmailLookupField from 'components/EmailLookupField'
import FieldsFactory from 'components/form/FieldsFactory'
import {FormActionGroups} from 'components/form/FormActionBar'
import {createValue} from 'components/form/utils'
import {Formik, FormikConsumer} from 'formik'
import {parseDayjs} from 'helpers/datetimeHelpers'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import withTranslate from 'modules/local/withTranslate'
import React, {useMemo} from 'react'
import {IoCalendarOutline} from 'react-icons/io5'
import {HiCheckBadge, HiUserCircle} from 'react-icons/hi2'
import {compose, mapProps, pure} from 'recompose'
import {GenderTypes} from 'views/AppLocals/appFieldTypes'
import {gConfigs} from 'views/AppLocals/configs'
import {SelectWidget} from 'views/AppLocals/custom/ReactSelectComponents'
import {createControlledFormField, createPhoneNumberField} from 'views/AppLocals/factory/createFormField'
import {NullAsDefault, shallowDiff} from 'views/Shared'
import Pure from '../../components/Pure'
import Translate from '../../modules/local/Translate'
import {appClassNames} from '../AppLocals/custom/appClassNames'
import getAvatar from '../../helpers/getAvatar'
import getAliasName from '../../helpers/getAliasName'
import getTitle from '../../helpers/getTitle'
import {Link} from 'react-router-dom'
import getLinkToDetail from '../../helpers/getLinkToDetail'
import {BsPersonFill} from 'react-icons/bs'
import dayjs from 'dayjs'
import {ActionModalWrapper} from '../../components/form/ActionModalWrapper'
import FormActionBar from '../../components/form/FormActionBar'
import {ChipGroup, Section} from '../../components/form/ModernForm'
import {roleIds} from '../../modules/permissions/contants'
import {
    digitLength,
    emailFormat,
    firstLetterUppercase,
    lengthBetween,
    noDoubleSpaces,
    noLeadingTrailingSpaces,
    noNumber,
    noSpecialChars,
    required,
    runRules,
} from '../../components/form/ValidateForm'

const member_modes = {
    organization: 'organization',
    others: 'others',
}

// ─── LinkedUserCard ──────────────────────────────────────────────────────────
// Hiện khi email đã được verify và khớp với user trên hệ thống
const LinkedUserCard = ({user, t}) => {
    if (!user?.name) return null
    const patronSaint = getAliasName(user?.owner, 'patron_saint')
    return (
        <div className="p-3 flex items-center gap-3 border border-green-400 rounded-xl">
            <Avatar
                size={40}
                src={getAvatar(user?.owner)}
                icon={<BsPersonFill size={20} className="text-color-500"/>}
                className="flex-shrink-0 flex flex-center background-200"
            />
            <div className="flex-1 flex flex-col min-w-0">
                <span className="font-semibold text-sm text-color-000 truncate">
                    {!!patronSaint && (
                        <span className="mr-1 font-medium text-xs text-color-400 italic capitalize">
                            {patronSaint}
                        </span>
                    )}
                    {getTitle(user)}
                </span>
                <Link
                    target="_blank"
                    to={getLinkToDetail(user?.owner || user)}
                    className="text-xs text-primary-500 no-underline italic leading-tight truncate">
                    @{getTitle(user)}
                </Link>
            </div>
            <Tag
                color="success"
                icon={<HiCheckBadge size={12} className="inline mr-1"/>}
                className="flex-shrink-0 text-xs">
                {t('linked')}
            </Tag>
        </div>
    )
}

// ─── GuestNotice ─────────────────────────────────────────────────────────────
// Hiện khi email không khớp user nào — sẽ thêm dưới dạng guest
const GuestNotice = ({t}) => (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg background-100 border border-dashed border-color-300">
        <HiUserCircle size={20} className="text-color-400 flex-shrink-0"/>
        <span className="text-xs text-color-500 italic">
            {t('email not linked to any account — will be added as guest')}
        </span>
    </div>
)

// ─── formSchema ───────────────────────────────────────────────────────────────
export const formSchema = {
    contact_email: {
        title: 'email',
        children: [
            {
                name: 'contact_email',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, form, translate, onChange}) => ({
                        name,
                        onChange,
                        placeholder: translate('ex: email'),
                        defaultValue: value,
                        // handleApply được gọi tự động bởi EmailLookupField (sau debounce + lookup)
                        handleApply: (user) => {
                            const {values, setValues} = form ?? {}
                            const {email, name, date_of_birth, gender, phone_number} = user ?? {}
                            const hasLinkedUser = !!user?.id

                            // Reset toàn bộ user-related fields trước
                            // Nếu dùng _.omitBy(_.isUndefined) thì khi email không tìm thấy,
                            // tất cả field undefined bị lọc → setValues merge {} → user cũ vẫn còn
                            const user_info = {
                                user_id: hasLinkedUser ? user.id : undefined,
                                user: hasLinkedUser ? user : undefined,   // reset về undefined nếu không có user
                                name: name ?? values?.name ?? undefined,
                                gender: gender ?? values?.gender ?? undefined,
                                phone_number: phone_number ?? values?.phone_number ?? undefined,
                                date_of_birth: date_of_birth
                                    ? parseDayjs(date_of_birth)
                                    : values?.date_of_birth ?? undefined,
                                contact_email: user?.fieldValue ?? email ?? undefined,
                            }

                            setValues({
                                ...values,
                                ...user_info,
                            })
                        },
                    }))
                )(EmailLookupField),
            },
        ],
    },
    name: {
        title: 'full name',
        children: [
            {
                name: 'name',
                component: createControlledFormField({
                    placeholder: 'name',
                    showCount: true,
                    maxLength: 120,
                }),
            },
        ],
    },
    contact_phone: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                label: 'phone',
                name: 'contact_phone',
                component: createPhoneNumberField({
                    placeholder: 'ex: phone',
                    Component: Input,
                }),
            },
        ],
    },
    date_of_birth__gender: {
        inline: true,
        className: appClassNames.inline_2,
        children: [
            {
                label: 'date of birth',
                name: 'date_of_birth',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        className: 'w-full',
                        format: gConfigs.dateFormat,
                        suffixIcon: <IoCalendarOutline className="text-color-500"/>,
                        placeholder: `${translate('date of birth')} "DD/MM/YYYY"`,
                        disabledDate: (current) => {
                            const maxDate = dayjs().subtract(4, 'year')
                            const minDate = dayjs().subtract(120, 'year')
                            return (
                                current &&
                                (current > maxDate.endOf('day') || current < minDate.startOf('day'))
                            )
                        },
                        defaultPickerValue: dayjs().subtract(4, 'year'),
                        onChange: (date) => {
                            onChange(createValue(name, date ? dayjs(new Date(date)) : null))
                        },
                    }))
                )(DatePicker),
            },
            {
                label: 'gender',
                name: 'gender',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, onChange, translate}) => ({
                        name,
                        value,
                        onChange: (value) => {
                            onChange(createValue(name, value))
                        },
                        options: Object.values(GenderTypes)
                    }))
                )(ChipGroup),
            },
        ],
    },
    member_groups: {
        title: 'member type',
        children: [
            {
                name: 'member_groups',
                component: ({name, value, onChange}) => (
                    <Translate>
                        {(t) => (
                            <SelectWidget
                                mode="tags"
                                name={name}
                                value={value}
                                allowClear={true}
                                className="w-full"
                                onChange={(value) => {
                                    onChange(createValue(name, value))
                                }}
                                placeholder={`${t('teacher')}; ${t('student')} ... (${t('grading levels placeholder')})`}
                            />
                        )}
                    </Translate>
                ),
            },
        ],
    },
    role_id: {
        title: 'role',
        children: [
            {
                name: 'role_id',
                component: compose(
                    withTranslate,
                    mapProps(({name, value, translate, onChange, form}) => {
                        let _roleCodes = {3: 'member'}
                        if (
                            !!form?.values?.available_actions?.switch_to_owner &&
                            !!form?.values?.available_actions?.set_role_admin
                        ) {
                            _roleCodes = {2: 'admin', 3: 'member'}
                        }
                        return {
                            name,
                            defaultValue: value,
                            allowClear: true,
                            className: 'w-full',
                            placeholder: translate('click to select'),
                            options: Object.keys(_roleCodes).map((e, i) => ({
                                key: i,
                                value: Number(e),
                                label: translate(roleIds[e]),
                                disabled: !!value && !!form?.values?.role_id && form?.values?.role_id === Number(e),
                            })),
                            onChange: (value) => {
                                onChange(createValue(name, value))
                            },
                        }
                    }),
                    pure
                )(SelectWidget),
            },
        ],
    },
}

// ─── Validation ───────────────────────────────────────────────────────────────
const validateEmail = (email, t) => {
    const rules = [required(t), emailFormat(t)].filter(Boolean)
    const message = runRules(email, rules)
    if (message) return {contact_email: message}
}

const validatePhoneNumber = (phoneNumber, t) => {
    if (!phoneNumber) return null
    const rules = [digitLength(10, 16, t)].filter(Boolean)
    const message = runRules(phoneNumber, rules)
    if (message) return {contact_phone: message}
}

const validateName = (name, t) => {
    const rules = [
        required(t),
        lengthBetween(2, 120, t),
        noNumber(t),
        noSpecialChars(t),
        noLeadingTrailingSpaces(t),
        noDoubleSpaces(t),
        firstLetterUppercase(t),
    ].filter(Boolean)
    const message = runRules(name, rules)
    if (message) return {name: message}
}

const validate = (t) => (values) => {
    const {contact_email, name, contact_phone} = values ?? {}
    return {
        ...validateEmail(contact_email, t),
        ...validateName(name, t),
        ...validatePhoneNumber(contact_phone, t),
    }
}

// ─── MemberCreateFrom ─────────────────────────────────────────────────────────
export const MemberCreateFrom = ({
                                     owner_id,
                                     owner_type,
                                     isSubmitting,
                                     onCancel = NullAsDefault,
                                     onSubmit = NullAsDefault,
                                 }) => {
    const t = useTranslate()

    const initialValues = useMemo(
        () => ({
            owner_id,
            owner_type,
            member_mode: member_modes.organization,
            gender: 3,
        }),
        [owner_id, owner_type]
    )


    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const {email, user_id, owner_id, owner_type, member_mode, member_groups, ...rest} = values ?? {}
                let params = {}
                if (member_mode === member_modes.others) {
                    params = {
                        member_groups: Array.from(member_groups ?? []).join(','),
                    }
                }
                onSubmit({...rest, ...params, user_id})
            }}>
            <ActionModalWrapper
                action={
                    <FormActionGroups
                        isSubmitting={isSubmitting}
                        submitProps={{label: 'invite'}}
                        resetProps={{hidden: true}}
                    />
                }>
                <FormikConsumer>
                    {({values, setValues}) => {
                        const {user} = values ?? {}
                        const hasLinkedUser = !!user?.name
                        // email đã nhập nhưng không tìm thấy user
                        const isGuestMode = !!values?.contact_email && !hasLinkedUser

                        return (
                            <div className="flex flex-col gap-4">
                                {/* Email lên ĐẦU form (trước "Thông tin cơ bản") - đây là bước "tìm
                                    trước" nên đặt trước bước "điền tay": gõ email xong EmailLookupField
                                    tự lookup + autofill hẳn Section bên dưới nếu khớp tài khoản có sẵn,
                                    đỡ phải gõ tay rồi bị ghi đè. subtitle nêu rõ ngay tại chỗ để khỏi
                                    phải đoán ý field email này khác các field còn lại (tự tìm, không chỉ
                                    là ô nhập liệu thường). */}
                                <Section title={t('email')} subtitle={t('search member by email hint')}>
                                    <Pure>
                                        <FieldsFactory
                                            formSchema={[
                                                formSchema.contact_email,
                                                formSchema.contact_phone
                                            ]}
                                        />
                                    </Pure>

                                    {/* Trạng thái: đã link user */}
                                    {hasLinkedUser && (
                                        <Pure>
                                            <LinkedUserCard user={user} t={t}/>
                                        </Pure>
                                    )}

                                    {/* Trạng thái: email không có tài khoản → guest */}
                                    {isGuestMode && !hasLinkedUser && (
                                        <Pure>
                                            <GuestNotice t={t}/>
                                        </Pure>
                                    )}
                                </Section>
                                <Section title={t('basic information')}>
                                    {/* Fields thông tin — luôn hiện để user có thể chỉnh (đã autofill
                                        từ email lookup ở trên nếu tìm thấy tài khoản) */}
                                    <Pure>
                                        <FieldsFactory
                                            formSchema={[
                                                formSchema.name,
                                                formSchema.date_of_birth__gender,
                                            ]}
                                        />
                                    </Pure>
                                </Section>
                            </div>
                        )
                    }}
                </FormikConsumer>
            </ActionModalWrapper>
        </Formik>
    )
}

// ─── MemberEditFrom ───────────────────────────────────────────────────────────
export const MemberEditFrom = ({
                                   item,
                                   owner_id,
                                   owner_type,
                                   isSubmitting,
                                   onCancel = NullAsDefault,
                                   onSubmit = NullAsDefault,
                               }) => {
    const t = useTranslate()

    const initialValues = useMemo(() => {
        const {user} = item ?? {}
        const _get = (name) => _.get(item, name, _.get(user, name))
        return {
            ...item,
            name: item?.name || _.get(user, 'name'),
            gender: _get('gender'),
            contact_email: _get('email'),
            contact_phone: _get('phone_number'),
            date_of_birth: parseDayjs(_get('date_of_birth')),
        }
    }, [item])

    return (
        <Formik
            validate={validate(t)}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const changes = shallowDiff(values, initialValues)
                const {name, gender, contact_email, contact_phone} = changes ?? {}
                onSubmit({name, gender, contact_email, contact_phone})
            }}>
            <ActionModalWrapper
                action={
                    <FormActionGroups
                        isSubmitting={isSubmitting}
                        submitProps={{label: 'save'}}
                    />
                }>
                <FormikConsumer>
                    {({values}) => {
                        const {user} = values ?? {}
                        return (
                            <div className="flex flex-col gap-4">
                                {/* Card user đã link (chỉ hiển thị, không cho thay đổi ở edit) */}
                                {!!user?.name && (
                                    <Pure>
                                        <LinkedUserCard user={user} t={t}/>
                                    </Pure>
                                )}
                                <Pure>
                                    <FieldsFactory
                                        formSchema={[
                                            formSchema.contact_email,
                                            formSchema.name,
                                        ]}
                                    />
                                </Pure>
                            </div>
                        )
                    }}
                </FormikConsumer>
            </ActionModalWrapper>
        </Formik>
    )
}

// ─── MemberRoleAssignFrom ─────────────────────────────────────────────────────
export const MemberRoleAssignFrom = ({
                                         item,
                                         onCancel = NullAsDefault,
                                         onSubmit = NullAsDefault,
                                     }) => {
    const t = useTranslate()

    const initialValues = useMemo(() => ({...item}), [item])

    const validateRole = (values) => {
        let errors = {}
        if (!!!values?.role_id) {
            errors.role_id = t('required field')
        }
        return errors
    }

    return (
        <Formik
            validate={validateRole}
            validateOnMount={true}
            validateOnChange={true}
            enableReinitialize={true}
            initialValues={initialValues}
            onSubmit={(values) => {
                const {role_id} = shallowDiff(values, initialValues) ?? {}
                onSubmit({role_id})
            }}>
            <div className="w-full flex flex-col gap-6">
                <Pure>
                    <FieldsFactory formSchema={[formSchema.role_id]}/>
                </Pure>
                <FormActionBar submitText="save" hideResetButton={true}/>
            </div>
        </Formik>
    )
}